import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '../theming';
import { Link } from '../Link';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

export type FooterLinkProps = {
  children: string;
  /**
   * Passing href parameter for the url
   */
  href: string;
  /**
   * Additional unique id for FooterLink component
   */
  id: string;
  /**
   * Handles the onClick functionality
   */
  onClick?: (id: string) => void;
  /**
   * Name of the footer section coming from footer-content.lib
   */
  sectionName: string;
  /**
   * The `rel` attribute defines the relationship between a linked resource and the current document.
   * Please see [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/rel) for the list of supported values.
   */
  rel?: HTMLAnchorElement['rel'];
};

const StyledFooterLink = styled(Link<'a'>)`
  margin: ${tokens.size.x1} ${tokens.size.x2};
  &::after {
    content: '·';
    position: relative;
    right: -0.5rem;
  }
`;

export const FooterLink = ({
  children,
  onClick,
  id,
  href,
  sectionName,
  rel,
}: FooterLinkProps): JSX.Element => (
  <StyledFooterLink
    href={href}
    id={`footer-${id}`}
    onClick={onClick ? (): void => onClick(id) : undefined}
    target="_blank"
    rel={rel}
    color="subdued"
    underlined={false}
    size={sectionName === 'secondary' ? 'xsmall' : 'default'}
    weight={sectionName === 'secondary' ? 'strong' : 'default'}
  >
    {children}
  </StyledFooterLink>
);

FooterLink.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  sectionName: PropTypes.string.isRequired,
  rel: PropTypes.string,
};
